import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Info, Layers, Settings } from "lucide-react";
import { FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { ErrorContent } from "../ErrorContent/ErrorContent";
import { NotFoundContent } from "../NotFoundContent";
import { useStore } from "@/hooks/useStore";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";
import { IAndITimePeriodsValue } from "@/config/date";

const MonitoringStatsSkeleton: FC = () => {
	return (
		<>
			<div className="flex flex-col items-center gap-4">
				<Skeleton className="h-12 w-40 rounded-md bg-blue-50 mt-5" />

				<Skeleton className="h-4 w-64 rounded-md bg-blue-50" />

				<Skeleton className="h-10 w-full rounded-md bg-blue-50" />
				<Skeleton className="h-9 w-full rounded-md bg-blue-50" />
			</div>
		</>
	);
};

export const MonitoringStatsCard = observer(() => {
	const {
		uiStore: { modalStore },
	} = useStore();

	const timePeriod: IAndITimePeriodsValue = "last30d";
	const totalApi = useAsyncStateStore(infiltrationAndInflow.getSummaries);
	const configuredApi = useAsyncStateStore(infiltrationAndInflow.getSummaries);

	const loadCounts = () => {
		totalApi.fetch({ pageNumber: 0, pageSize: 0 }, timePeriod, undefined, { isActive: true });
		configuredApi.fetch({ pageNumber: 0, pageSize: 0 }, timePeriod, undefined, {
			isFullyConfiguredForIAndI: true,
			isActive: true,
		});
	};

	useEffect(() => {
		loadCounts();
	}, []);

	const loading = totalApi.loading || configuredApi.loading;
	const loaded = totalApi.loaded && configuredApi.loaded;
	const error = totalApi.error || configuredApi.error;
	const total = totalApi.data?.query?.pagination?.totalItems ?? 0;
	const configured = configuredApi.data?.query?.pagination?.totalItems ?? 0;
	const notConfigured = Math.max(0, total - configured);
	const isNoData = loaded && !error && total === 0;

	return (
		<Card className="shadow-sm border border-muted rounded-xl flex flex-col h-full drop-shadow-sm">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold flex items-center gap-2">
					<Layers className="h-4 w-4 text-purple-500" />
					System Overview
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-1 flex flex-col justify-center">
				{loading ? (
					<MonitoringStatsSkeleton />
				) : isNoData ? (
					<NotFoundContent message="No monitoring data available" />
				) : error ? (
					<ErrorContent
						error={error}
						notFoundMessage="No monitoring data available"
						message="Unable to load monitoring statistics"
					/>
				) : (
					<>
						<div className="text-center mb-3">
							<div className="text-3xl font-bold text-primary leading-tight">{configured}</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								of <span className="text-foreground font-semibold">{total}</span> structures configured
							</div>
						</div>

						<div className="relative bg-muted/50 p-2.5 rounded-md text-[11px] text-muted-foreground mb-2">
							<Info className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
							<p className="text-center font-medium pl-6">
								{notConfigured} structures are not yet configured for infiltration and inflow
							</p>
						</div>

						<Button
							size="sm"
							className="w-full text-xs font-medium"
							disabled={notConfigured === 0}
							onClick={() => modalStore.open("structurePropertiesWizard", { onClose: loadCounts })}
						>
							<Settings className="mr-2 h-4 w-4" />
							Configure Structures
						</Button>
					</>
				)}
			</CardContent>
		</Card>
	);
});
